import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import ToolFavoriteHeart from '@/components/Tools/ToolFavoriteHeart';
import { OUTPUT_TYPE_ICON, outputTypeLabel } from '@/components/Tools/ToolCard';
import { colorForSlug, hexToRgba, paletteForSlug, formatCount } from '@/utils/agent_catalog';

const isWeb = Platform.OS === 'web';
const MIN_USAGE_TO_SHOW = 25;
const SIDE_CARD_COUNT = 4;
// Below this measured width the lead card can't sit beside a 2×2 of side
// cards without crushing them, so the section stacks. Measured (not window)
// width matters because the page can render inside a narrow column next to
// an open side panel.
const SIDE_BY_SIDE_MIN_WIDTH = 900;

const categorySlugOf = (tool) =>
  tool?.category?.slug || (typeof tool?.category === 'string' ? tool.category : null) || '';

const usageOf = (tool) => Number(tool?.stats?.usageCount) || 0;

// ─── Lead card ──────────────────────────────────────────────────────────────

function LeadCard({ tool, onPress, colors, isXS, isSM, isAltBGColor }) {
  const [hovered, setHovered] = useState(false);
  const slug = categorySlugOf(tool);
  const hex = colorForSlug(slug);
  const palette = paletteForSlug(slug);
  const usage = usageOf(tool);
  const avatarSize = isXS ? 60 : 76;
  const baseBg = isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block;

  return (
    <Pressable
      onPress={() => onPress?.(tool)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={tool?.name}
      style={{
        flex: isSM ? undefined : 1.15,
        width: isSM ? '100%' : undefined,
        minHeight: isXS ? 230 : isSM ? 250 : 320,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: hovered ? palette.fg : colors.border_color_secondary,
        backgroundColor: baseBg,
        overflow: 'hidden',
        ...(isWeb && {
          cursor: 'pointer',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          transform: hovered ? 'translateY(-2px)' : 'none',
          boxShadow: hovered ? `0 24px 48px -28px ${hexToRgba(hex, 0.55)}` : 'none',
        }),
      }}
    >
      <LinearGradient
        colors={[hexToRgba(hex, 0.22), hexToRgba(hex, 0.06), hexToRgba(hex, 0)]}
        locations={[0, 0.55, 1]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {/* Big ghosted glyph in the corner anchors the card without stock art. */}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          right: isXS ? -26 : -18,
          bottom: isXS ? -30 : -22,
          opacity: 0.08,
          transform: [{ rotate: '-10deg' }],
        }}
      >
        <Icon
          name={tool?.icon || OUTPUT_TYPE_ICON[tool?.outputType] || 'wand-magic-sparkles'}
          size={isXS ? 150 : 210}
          color={hex}
          solid
        />
      </View>

      <View style={{ flex: 1, padding: isXS ? 18 : 26, justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <View style={{ borderRadius: 20, overflow: 'hidden' }}>
            <ToolAvatar
              tool={tool}
              size={avatarSize}
              iconSize={Math.round(avatarSize * 0.46)}
              borderRadius={20}
              backgroundColor={hexToRgba(hex, 0.16)}
              color={palette.fg}
            />
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {usage >= MIN_USAGE_TO_SHOW ? (
              <View
                style={{
                  flexDirection: 'row', alignItems: 'center', gap: 6,
                  height: 26, paddingHorizontal: 10, borderRadius: 999,
                  backgroundColor: hexToRgba(hex, 0.14),
                }}
              >
                <Icon name="bolt" size={10} color={palette.fg} solid />
                <Text size="xs" weight="bold" style={{ color: palette.fg, fontVariant: ['tabular-nums'] }}>
                  {formatCount(usage)} runs
                </Text>
              </View>
            ) : null}
            <ToolFavoriteHeart tool={tool} />
          </View>
        </View>

        <View style={{ marginTop: isXS ? 18 : 28, maxWidth: 520 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {tool?.category?.name ? (
              <Text size="xxs" weight="bold" style={{ color: palette.fg, letterSpacing: 1, textTransform: 'uppercase' }}>
                {tool.category.name}
              </Text>
            ) : null}
            {tool?.category?.name && tool?.outputType ? (
              <View style={{ width: 3, height: 3, borderRadius: 2, backgroundColor: colors.text_color_tertiary }} />
            ) : null}
            {tool?.outputType ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Icon name={OUTPUT_TYPE_ICON[tool.outputType] || 'circle'} size={10} color={colors.text_color_tertiary} />
                <Text size="xxs" weight="bold" color="tertiary" style={{ letterSpacing: 1, textTransform: 'uppercase' }}>
                  {outputTypeLabel(tool.outputType)}
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            tag="h3"
            weight="extraBold"
            numberOfLines={2}
            style={{
              fontSize: isXS ? 24 : 30,
              lineHeight: isXS ? 28 : 34,
              letterSpacing: -0.8,
              // A global stylesheet centers `h3` on web; this is a left-aligned card title.
              textAlign: 'left',
              color: colors.text_color_primary,
            }}
          >
            {tool?.name}
          </Text>
          <Text
            color="secondary"
            numberOfLines={isXS ? 2 : 3}
            style={{ marginTop: 8, fontSize: isXS ? 14 : 15, lineHeight: isXS ? 20 : 23 }}
          >
            {tool?.shortDescription || tool?.description}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: isXS ? 14 : 20 }}>
            <View
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 8,
                height: 38, paddingHorizontal: 16, borderRadius: 10,
                backgroundColor: hovered ? palette.fg : colors.text_color_primary,
                ...(isWeb && { transition: 'background-color 0.2s ease' }),
              }}
            >
              <Text size="s" weight="bold" style={{ color: colors.solid_color_primary }}>Open tool</Text>
              <Icon name="arrow-right" size={12} color={colors.solid_color_primary} />
            </View>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

// ─── Side card ──────────────────────────────────────────────────────────────

function SideCard({ tool, onPress, colors, isXS, isAltBGColor, fixedWidth = null }) {
  const [hovered, setHovered] = useState(false);
  const slug = categorySlugOf(tool);
  const palette = paletteForSlug(slug);
  const usage = usageOf(tool);
  const avatarSize = 46;

  return (
    <Pressable
      onPress={() => onPress?.(tool)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={tool?.name}
      style={{
        ...(fixedWidth ? { width: fixedWidth } : { flex: 1, minWidth: 0 }),
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
        padding: isXS ? 14 : 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: hovered ? palette.fg : colors.border_color_secondary,
        backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
        ...(isWeb && {
          cursor: 'pointer',
          transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
          transform: hovered ? 'translateY(-2px)' : 'none',
          boxShadow: hovered ? '0 14px 30px -18px rgba(0,0,0,0.35)' : 'none',
        }),
      }}
    >
      <View style={{ borderRadius: 13, overflow: 'hidden', flexShrink: 0 }}>
        <ToolAvatar
          tool={tool}
          size={avatarSize}
          iconSize={Math.round(avatarSize * 0.46)}
          borderRadius={13}
          backgroundColor={palette.bg}
          color={palette.fg}
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
          {tool?.category?.name ? (
            <Text size="xxs" weight="bold" numberOfLines={1} style={{ color: palette.fg, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              {tool.category.name}
            </Text>
          ) : <View />}
          {usage >= MIN_USAGE_TO_SHOW ? (
            <Text size="xxs" color="tertiary" style={{ fontVariant: ['tabular-nums'] }}>
              {formatCount(usage)} runs
            </Text>
          ) : null}
        </View>
        <Text weight="bold" size="m" numberOfLines={1} style={{ letterSpacing: -0.2 }}>
          {tool?.name}
        </Text>
        <Text size="s" color="secondary" numberOfLines={2} style={{ marginTop: 3, lineHeight: 18 }}>
          {tool?.shortDescription || tool?.description}
        </Text>
      </View>
    </Pressable>
  );
}

// ─── Section ────────────────────────────────────────────────────────────────

/**
 * Featured bento: the most-used featured tool gets a large gradient lead card,
 * the next four sit beside it in a 2×2. On phones the lead stacks on top and
 * the side cards become a horizontal strip.
 */
function ToolsSpotlight({ tools, onToolPress, title = 'Featured', subtitle = null, style }) {
  const { colors, theme } = useTheme();
  const { isXS, isSM: isSMWindow, windowWidth } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const [measuredWidth, setMeasuredWidth] = useState(null);

  const onLayout = useCallback((e) => {
    const w = e?.nativeEvent?.layout?.width;
    if (typeof w === 'number' && w > 0) setMeasuredWidth(Math.round(w));
  }, []);

  const { lead, side } = useMemo(() => {
    const list = Array.isArray(tools) ? tools.filter(Boolean) : [];
    if (!list.length) return { lead: null, side: [] };
    const sorted = [...list].sort((a, b) => usageOf(b) - usageOf(a));
    return { lead: sorted[0], side: sorted.slice(1, 1 + SIDE_CARD_COUNT) };
  }, [tools]);

  if (!lead) return null;

  // "Stacked" = lead on top, side cards below. Window breakpoint handles
  // tablets; the measured width handles a wide window with a narrow column.
  const isSM = isSMWindow || (measuredWidth != null && measuredWidth < SIDE_BY_SIDE_MIN_WIDTH);
  const gap = isXS ? 10 : 14;
  const sideCardWidth = isXS ? Math.min(300, Math.max(240, windowWidth - 70)) : null;

  const renderSide = () => {
    if (!side.length) return null;
    if (isXS) {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ marginHorizontal: -15, marginTop: gap }}
          contentContainerStyle={{ flexDirection: 'row', gap, paddingHorizontal: 15 }}
        >
          {side.map((tool) => (
            <SideCard
              key={tool.id || tool._id || tool.slug}
              tool={tool}
              onPress={onToolPress}
              colors={colors}
              isXS={isXS}
              isAltBGColor={isAltBGColor}
              fixedWidth={sideCardWidth}
            />
          ))}
        </ScrollView>
      );
    }
    const rows = [side.slice(0, 2), side.slice(2, 4)].filter((r) => r.length);
    return (
      <View style={{ flex: isSM ? undefined : 1, width: isSM ? '100%' : undefined, gap, marginTop: isSM ? gap : 0 }}>
        {rows.map((row, i) => (
          <View key={i} style={{ flexDirection: 'row', gap, flex: isSM ? undefined : 1 }}>
            {row.map((tool) => (
              <SideCard
                key={tool.id || tool._id || tool.slug}
                tool={tool}
                onPress={onToolPress}
                colors={colors}
                isXS={isXS}
                isAltBGColor={isAltBGColor}
              />
            ))}
            {row.length === 1 ? <View style={{ flex: 1 }} /> : null}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={[{ width: '100%' }, style]} onLayout={onLayout}>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <Text tag="h2" weight="extraBold" style={{ fontSize: isXS ? 18 : 20, letterSpacing: -0.4 }}>
          {title}
        </Text>
        {subtitle ? <Text size="s" color="tertiary">{subtitle}</Text> : null}
      </View>
      <View style={{ flexDirection: isSM ? 'column' : 'row', gap: isSM ? 0 : gap, alignItems: 'stretch' }}>
        <LeadCard
          tool={lead}
          onPress={onToolPress}
          colors={colors}
          isXS={isXS}
          isSM={isSM}
          isAltBGColor={isAltBGColor}
        />
        {renderSide()}
      </View>
    </View>
  );
}

export default React.memo(ToolsSpotlight);
